/**
 * PenthouseHero — Via6 property-level page hero for /penthouse.
 *
 * Replaces the inherited generic `PageHero` on the penthouse page with a
 * dedicated slot that matches the visual treatment used by the other
 * per-page heroes in this theme (FRCHero, PetPolicyHero, FloorPlansHero,
 * etc.). Renders a hero image with the page title left-aligned in the
 * visible band below the fixed nav.
 *
 * Visual + behaviour come from the shared `_PageImageHeroBody` helper
 * exported by the parent Via6 theme — same body the sibling *Hero slots
 * use, so any future tweak there propagates here automatically. This
 * file owns the slot registration, the editable `contentSchema`, and
 * the hidden literal-path anchors the schema linter relies on.
 */
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { PageImageHeroBody } from '@vibesites/theme-via6/components/_PageImageHeroBody';

// Background image definition. Mirrors the schema's slotGroups entry
// below — the shared body uses this same literal at runtime so the
// editor sidebar and the rendered slot stay in lockstep.
const bgDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Penthouse hero image',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

export function PenthouseHero() {
  return (
    <>
      <PageImageHeroBody
        titlePath="penthousehero.title"
        bgPath="penthousehero.bg_image"
        bgDef={bgDef}
        defaultTitle="Penthouse"
        keywords={['penthouse', 'top floor', 'luxury', 'view', 'skyline', 'interior']}
        pageHint="penthousehero"
        altText="Penthouse hero image"
      />

      {/* Schema-lint anchors — hidden, never interactive.
          The visible JSX above lives inside `PageImageHeroBody`, which
          uses the dynamic paths above. The schema linter only scans this
          file's source for literal `path=` / `groupPath=` strings, so we
          duplicate them here as hidden anchors. See
          reference/shared-component-pattern.md for the rationale. */}
      <RichTextContent
        path="penthousehero.title"
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <EditableSlotGroup
        groupPath="penthousehero.bg_image"
        definition={bgDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PenthouseHero',
  displayName: 'Penthouse Hero',
  tokens: {
    'penthousehero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: 'Penthouse',
    },
  },
  slotGroups: {
    'penthousehero.bg_image': {
      label: 'Background Image',
      fields: {
        image: {
          label: 'Image',
          type: 'image',
          defaultField: 'property_featured_image',
        },
        alt: {
          label: 'Alt Text',
          type: 'text',
          defaultValue: 'Penthouse hero image',
        },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 0,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
    },
  },
};

(PenthouseHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PenthouseHero;
